import React from 'react';
import { observer } from 'mobx-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import OETCell from './OETCell';
import DndTypes from './DnDTypes';
import { canDropRowOnRow, useOETDragState } from './dndContext';
import type { RowDragItem } from './DnDTypes';
import type { IOETRow } from '../../stores/model/uimodel/oetrow';
import type { IOETColumn } from '../../stores/model/uimodel/oetcolumn';
import type { IObjectType } from '../../stores/model/metamodel/object_type';

/** The own (caller-supplied) props an OET row reads. */
export interface OETRowProps {
  columns: IOETColumn[];
  row: IOETRow;
  selectable: boolean;
  onSelect: (id: string) => void;
  onRowHeaderClick: (eventType: string) => void;
  onCellClick: (eventType: string, objectType: string) => void;
  selected: string | null;
  highlightedMethods: string[];
  popovers: boolean;
  draggedColumn: string | null;
}

/**
 * A row in the OET.
 *
 * A row is both a drag source and a drop target. The drag item carries the row
 * `index`, its dnd `group` and its specialisation `level`. Dropping a row onto
 * this row moves it to *below* this row (`to = row.index + 1`), or to the top of
 * the sub-rows when this row is the dragged row's parent (`to = 0`) — the
 * concrete move is computed in the container's `onDragEnd`. The hover highlight
 * (`highlight-level-N`) is painted from the *dragged* row's level.
 *
 * - columns: the columns in the OET
 * - row: the OET row
 * - selectable: is selection enabled for the event type in the row?
 * - onSelect: callback to call when the event type is selected. Arguments: id.
 * - onRowHeaderClick: callback when the event type is clicked while selection is disabled.
 * - onCellClick: callback when a cell is clicked. Arguments: event type id, object type id.
 * - selected: id of the currently selected element.
 * - highlightedMethods: the currently highlighted methods.
 * - popovers: are (method) popovers enabled?
 * - draggedColumn: id of the currently dragged column.
 */
const OETRow = observer(function OETRow({
  row,
  columns,
  selectable,
  onSelect,
  onRowHeaderClick,
  onCellClick,
  selected,
  highlightedMethods,
  popovers,
  draggedColumn,
}: OETRowProps) {
  const dragItem: RowDragItem = {
    type: DndTypes.ROW,
    index: row.index,
    group: row.dndGroup,
    level: row.event.specialisationLevel,
  };

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `oet-row-src-${row.event.id}`,
    data: dragItem,
  });
  const { setNodeRef: setDropRef } = useDroppable({
    id: `oet-row-tgt-${row.event.id}`,
    data: {
      kind: 'row',
      rowEventId: row.event.id,
      rowGroup: row.dndGroup,
      rowIndex: row.index,
    },
  });

  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  const { activeItem, overId } = useOETDragState();
  const isOver = overId === `oet-row-tgt-${row.event.id}`;
  const canDrop =
    activeItem != null &&
    activeItem.type === DndTypes.ROW &&
    canDropRowOnRow(activeItem, row.event.id, row.dndGroup, row.index);
  // The drop-target highlight reads the *dragged* row's level (the react-dnd
  // `monitor.getItem().level`), painted on this target row.
  const isOverLevel =
    isOver && activeItem != null && activeItem.type === DndTypes.ROW ? activeItem.level : undefined;

  const classes: string[] = [];
  if (selectable) {
    classes.push('selectable');
  }
  if (selected === row.event.id) {
    classes.push('selected');
  }
  const rowClasses: string[] = [];
  if (isDragging) {
    rowClasses.push('dragging');
  }
  if (isOver && canDrop) {
    rowClasses.push('drop-highlight');
  }
  if (isOverLevel != null && canDrop) {
    rowClasses.push(`highlight-level-${isOverLevel}`);
  }
  if (row.event.specialised) {
    classes.push('specialised');
    rowClasses.push('specialised');
  }
  const otherRowProps = { 'data-level': row.event.specialisationLevel };

  return (
    <tr ref={setNodeRef} className={rowClasses.join(' ')} {...otherRowProps}>
      <th
        className={classes.join(' ')}
        onClick={
          selectable ? () => onSelect(row.event.id) : () => onRowHeaderClick(row.event.id)
        }
        {...listeners}
        {...attributes}
      >
        {row.event.specialised && (
          <span className="arrows">
            {/* Keep the legacy `Array.apply(null, { length })` sparse-array
                idiom byte-identical rather than rewriting it to a spread. */}
            {/* eslint-disable-next-line prefer-spread */}
            {Array.apply(
              null,
              { length: row.event.specialisationLevel } as unknown as unknown[]
            ).map((e, i) => (
              <span className="arrow" key={i}>
                {'→ '}
              </span>
            ))}
          </span>
        )}
        {row.event.name}
      </th>
      {columns.map((c) => {
        return (
          <OETCell
            key={c.object.id}
            // `IOETColumn` is `Instance<typeof OETColumn>`, so its `object`
            // reference reads as the loose `modelReference` type (the MPC
            // `type` enum widens to `string`); the resolved node is a real
            // `IObjectType`. Restore the concrete type at this read boundary.
            object={c.object as unknown as IObjectType}
            event={row.event}
            selectable={selectable}
            onSelect={onSelect}
            selected={selected}
            highlightedMethods={highlightedMethods}
            popovers={popovers}
            onClick={onCellClick}
            isDragging={draggedColumn === c.id}
          />
        );
      })}
    </tr>
  );
});

export default OETRow;
